import { Component, inject } from '@angular/core';
import { BaseResultTemplateComponent } from "../../base-result-template.component";
import { CommonModule } from "@angular/common";
import { TranslateModule } from "@ngx-translate/core";
import { ReiseAufVariables } from "../reise-auf.variables";
import { RxState, selectSlice } from "@rx-angular/state";
import { ActivitiContentService } from "@alfresco/adf-core";
import { of } from "rxjs";
import { MatButtonModule } from "@angular/material/button";
import moment from "moment-es6";
import { saveAs } from 'file-saver';
import { fromPromise } from "rxjs/internal-compatibility";
import { switchMap } from "rxjs/operators";
import { WorkflowResultProtocol } from "@alf-nx-workspace/shared/interfaces";
import { WorkflowApprover } from "@alf-nx-workspace/flow/interfaces";

interface ResultTemplateComponentState {
  variableMap: Map<string, string>,
  workflowResult: string,
  approved: WorkflowApprover[],
  rejectedBy: string,
  rejectedReason: string,
  contentId: number,
  protocol: WorkflowResultProtocol[]
}

@Component({
  selector: 'reise-auf-result-template',
  templateUrl: './result-template.component.html',
  styleUrls: ['./result-template.component.css'],
  standalone: true,
  imports: [CommonModule, TranslateModule, MatButtonModule],
  providers: [RxState]
})
export class ResultTemplateComponent extends BaseResultTemplateComponent {

  readonly activitiContentService = inject(ActivitiContentService);
  readonly fields = ReiseAufVariables;
  readonly rxState = inject<RxState<ResultTemplateComponentState>>(RxState);
  readonly vm$ = this.rxState.select(selectSlice(["workflowResult", "approved", "rejectedBy", "rejectedReason", "protocol"]));

  private filename;

  ngOnInit() {
    this.connectState();
    this.sideEffects();
  }

  private connectState() {
    this.rxState.connect("variableMap", this.variableMapSubject);
    this.rxState.connect("workflowResult", this.workflowResultSubject);

    this.rxState.connect("contentId", this.variableMapSubject, (oldState, value) => {
      return parseInt(value.get(this.fields.reiseauftragFormularAbschluss));
    });
  }

  private sideEffects() {

    this.rxState.hold(this.rxState.select('workflowResult'), (result) => {
      const variableMap = this.rxState.get('variableMap');

      if(result === this.workflowResults.successful) {
        const approved = [];
        if (variableMap.has(this.fields.kostenverantwortlicherId)) {
          const fullName = variableMap.get(this.fields.kostenverantwortlicherNachname) + " " + variableMap.get(this.fields.kostenverantwortlicherVorname);
          const date = variableMap.has(this.fields.datumFreigabeKostenverantwortlicher) ? moment(variableMap.get(this.fields.datumFreigabeKostenverantwortlicher), 'DD.MM.YYYY HH:mm Z').toDate() : null;
          approved.push({by: fullName.toUpperCase(), on: date})
        }
        if (variableMap.has(this.fields.vorgesetzterId)) {
          const fullName = variableMap.get(this.fields.vorgesetzterNachname) + " " + variableMap.get(this.fields.vorgesetzterVorname);
          const date = variableMap.has(this.fields.datumFreigabeVorgesetzter) ? moment(variableMap.get(this.fields.datumFreigabeVorgesetzter), 'DD.MM.YYYY HH:mm Z').toDate() : null;
          approved.push({by: fullName.toUpperCase(), on: date})
        }
        if (variableMap.has(this.fields.zusaetzlich)) {
          const fullName = variableMap.get(this.fields.zusatzPersonNachname) + " " + variableMap.get(this.fields.zusatzPersonVorname);
          const date = variableMap.has(this.fields.datumFreigabeZusatzlich) ? moment(variableMap.get(this.fields.datumFreigabeZusatzlich), 'DD.MM.YYYY HH:mm Z').toDate() : null;
          approved.push({by: fullName.toUpperCase(), on: date})
        }
        this.rxState.set('approved', () => approved);
      } else if (result === this.workflowResults.unsuccessful) {
        const rejected = [];
        if(variableMap.get(this.fields.aktuelleFreigabe) === 'kostenverantworlicher') {
          const fullName = variableMap.get(this.fields.kostenverantwortlicherNachname) + " " + variableMap.get(this.fields.kostenverantwortlicherVorname);
          this.rxState.set('rejectedBy', () => fullName);
        } else if (variableMap.get(this.fields.aktuelleFreigabe) === 'vorgesetzter') {
          const fullName = variableMap.get(this.fields.vorgesetzterNachname) + " " + variableMap.get(this.fields.vorgesetzterVorname);
          this.rxState.set('rejectedBy', () => fullName);
        }
        this.rxState.set('rejectedReason', () => variableMap.get(this.fields.verbesserungsauftrag));
      }

      this.rxState.connect("protocol", this.variableMapSubject, (oldState, value) => {
        try {
          return JSON.parse(this.variableMap.get(this.fields._bokuFlow_workflowResultProtocolJSON));
        } catch (e) {
          return [];
        }
      });
    })
  }

  downloadContent($event: MouseEvent) {
    $event.preventDefault();

    const contentId = this.rxState.get('contentId');
    fromPromise(this.activitiContentService.contentApi.getContent(contentId))
      .pipe(
        switchMap(({ name }) => of(name)),
        switchMap((name) => {
          this.filename = name;
          return this.activitiContentService.contentApi.getRawContent(contentId);
        })
      )
      .subscribe((content) => saveAs(content, this.filename));
  }

  getContentUrl(): string {
    return this.activitiContentService.contentApi.getRawContentUrl(this.rxState.get('contentId'));
  }
}
